import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Keeps the app shell's side drawers mutually exclusive — only one open at a time — and holds the
 * one width they share, so switching drawer type (e.g. dial-logs → alerts) keeps the panel the same
 * size instead of each drawer snapping to its own remembered width.
 *
 * Each drawer service reports its own opens here, closes itself when another drawer opens, and reads
 * / writes its width through here. It lives in shared/services (not in either drawer) so both the
 * alert-detail drawer (shared) and the dial-logs drawer (a feature lib) can use it without one
 * depending on the other — a feature may import shared, but shared may not import a feature.
 */
@Injectable({ providedIn: 'root' })
export class SideDrawerCoordinatorService {
    // One width for every shell drawer. Range spans both drawers' old bounds so neither is pinched.
    static readonly MIN_WIDTH = 460;
    static readonly MAX_WIDTH = 1200;
    private static readonly DEFAULT_WIDTH = 760;
    private static readonly STORAGE_KEY = 'sideDrawer.width';

    private readonly _opened = new Subject<string>();
    private readonly _width = new BehaviorSubject<number>(this.loadWidth());
    private readonly _closeAll = new Subject<void>();

    /** Emits the id of a drawer as it opens; every other drawer closes on the emission. */
    readonly opened$ = this._opened.asObservable();

    /** The shared drawer width; every drawer host renders this. */
    readonly width$ = this._width.asObservable();

    /** Emits on route navigation; every open drawer closes (a drawer belongs to the page it was
     * opened on, so it shouldn't linger onto the next page). */
    readonly closeAll$ = this._closeAll.asObservable();

    constructor(router: Router) {
        router.events
            .pipe(filter((e) => e instanceof NavigationStart))
            .subscribe(() => this._closeAll.next());
    }

    /** A drawer calls this from its own open() so the others can stand down. */
    notifyOpened(drawerId: string): void {
        this._opened.next(drawerId);
    }

    setWidth(px: number): void {
        const w = this.clamp(px);
        this._width.next(w);
        try {
            localStorage.setItem(SideDrawerCoordinatorService.STORAGE_KEY, String(w));
        } catch {
            /* localStorage unavailable — width just won't persist */
        }
    }

    private clamp(px: number): number {
        return Math.min(
            SideDrawerCoordinatorService.MAX_WIDTH,
            Math.max(SideDrawerCoordinatorService.MIN_WIDTH, Math.round(px))
        );
    }

    private loadWidth(): number {
        try {
            const v = Number(localStorage.getItem(SideDrawerCoordinatorService.STORAGE_KEY));
            if (Number.isFinite(v) && v > 0) {
                return this.clamp(v);
            }
        } catch {
            /* ignore */
        }
        return SideDrawerCoordinatorService.DEFAULT_WIDTH;
    }
}
